import { useState, useEffect, useMemo } from 'react';
import { Loader2, Search, Info, FileText, X, Medal } from 'lucide-react';
import { certificatesApi } from '../api';

type Row = {
    certificate_id: number;
    program_name: string;
    program_code: string;
    batch_code: string;
    serial_no: string | null;
    is_published: boolean;
    published_at: string | null;
};



export default function App() {
    const [rows, setRows] = useState<Row[]>([]);
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        // Fetch certificates on component mount
        const fetchCertificates = async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await certificatesApi.listMine();
                if (res.data?.success && res.data.data?.rows) {
                    setRows(res.data.data.rows);
                }
            } catch (e) {
                console.error(e);
                setError('Failed to load certificates. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchCertificates();
    }, []);

    const filtered = useMemo(() => {
        const t = q.trim().toLowerCase();
        if (!t) return rows;
        return rows.filter(r =>
            r.batch_code.toLowerCase().includes(t) ||
            r.program_name.toLowerCase().includes(t) ||
            (r.serial_no ?? '').toLowerCase().includes(t)
        );
    }, [rows, q]);


    const handleAction = async (action: 'view' | 'download', certificateId: number) => {
        try {
            setError(null);
            if (action === 'view') {
                const resp = await certificatesApi.viewOne(certificateId);
                const url = resp.data?.data?.url;
                if (url) window.open(url, '_blank', 'noopener');
                setSuccess('Certificate opened successfully.');
            } else {
                const resp = await certificatesApi.donwoadlOneCert(certificateId);
                const url = resp.data?.data?.url;
                if (url) {
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `certificate-${certificateId}.pdf`); // Force download with a filename
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
                setSuccess('Certificate download initiated.');
            }
        } catch (e) {
            console.error(e);
            setError(`Unable to ${action} certificate.`);
        }
    };

    return (
        <div className="bg-slate-50 min-h-screen font-sans p-4 md:p-8">
            {/* Notifications */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{error}</span>
                    <button
                        className="absolute top-0 bottom-0 right-0 px-4 py-3"
                        onClick={() => setError(null)}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}
            {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{success}</span>
                    <button
                        className="absolute top-0 bottom-0 right-0 px-4 py-3"
                        onClick={() => setSuccess(null)}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}
            <div className="max-w-6xl mx-auto">
                {/* Header Section */}
                <header className="mb-8 p-6 bg-white rounded-2xl shadow-md">
                    <h1 className="text-4xl font-extrabold text-gray-900 mb-2">My Certificates</h1>
                    <p className="text-base text-gray-500">
                        View and download your digital certificates.
                    </p>
                </header>

                {/* Search Bar */}
                <div className="mb-6">
                    <div className="relative">
                        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by program, batch, or serial number..."
                            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 ease-in-out text-gray-700 shadow-sm"
                        />
                    </div>
                </div>

                {/* Conditional Rendering based on state */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center p-12 bg-white rounded-2xl shadow-md">
                        <Loader2 className="animate-spin h-10 w-10 text-indigo-500" />
                        <p className="mt-4 text-gray-600 font-medium">Loading certificates...</p>
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center p-8 bg-red-50 border border-red-200 text-red-700 rounded-2xl shadow-md">
                        <Info size={24} className="mr-3 text-red-500" />
                        <p className="font-medium">{error}</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-12 bg-white rounded-2xl shadow-md">
                        <FileText size={48} className="text-gray-400 mb-4" />
                        <h3 className="text-xl font-semibold text-gray-800">No Certificates Found</h3>
                        <p className="mt-2 text-gray-500">
                            {q ? "Your search did not match any certificates." : "It looks like you don't have any certificates yet."}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto bg-white rounded-2xl shadow-md">
                        <table className="w-full whitespace-nowrap divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                                        Program
                                    </th>
                                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                                        Batch
                                    </th>
                                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                                        Serial
                                    </th>
                                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                                        Published
                                    </th>
                                    <th scope="col" className="relative px-6 py-4">
                                        <span className="sr-only">Actions</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filtered.map(r => (
                                    <tr key={r.certificate_id} className="hover:bg-gray-50 transition-colors duration-200">
                                        <td className="px-6 py-5 text-sm">
                                            <div className="font-semibold text-gray-900">{r.program_name}</div>
                                            <div className="text-gray-500 text-xs mt-0.5">{r.program_code}</div>
                                        </td>
                                        <td className="px-6 py-5 text-sm text-gray-700">{r.batch_code}</td>
                                        <td className="px-6 py-5 text-sm text-gray-700">{r.serial_no || '—'}</td>
                                        <td className="px-6 py-5 text-sm">
                                            {r.is_published ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    {r.published_at ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(r.published_at)) : 'Published'}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                                    Pending
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-5 text-right space-x-2">
                                            <button
                                                onClick={() => handleAction('view', r.certificate_id)}
                                                disabled={!r.is_published}
                                                className={`inline-flex items-center p-2 rounded-full border border-gray-300 text-gray-700 transition duration-150 ease-in-out ${r.is_published ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'}`}
                                                title={r.is_published ? 'View / Download Certificate' : 'Not published yet'}
                                            >
                                                <Medal size={18} />
                                            </button>
                                            {/* <button
                                                onClick={() => handleAction('view', r.certificate_id)}
                                                disabled={!r.is_published}
                                                className={`inline-flex items-center p-2 rounded-full border border-gray-300 text-gray-700 transition duration-150 ease-in-out ${r.is_published ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'}`}
                                                title={r.is_published ? 'View Certificate' : 'Not published yet'}
                                            >
                                                <Eye size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleAction('download', r.certificate_id)}
                                                disabled={!r.is_published}
                                                className={`inline-flex items-center p-2 rounded-full border border-gray-300 text-gray-700 transition duration-150 ease-in-out ${r.is_published ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'}`}
                                                title={r.is_published ? 'Download Certificate' : 'Not published yet'}
                                            >
                                                <Download size={18} />
                                            </button> */}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
